import { useEffect, useMemo, useState, Fragment } from "react";
import axios from "axios";
import { Loader2, RefreshCcw, CalendarDays, Clock, Phone, MapPin, Truck, CheckCircle2, Users, Wallet, Inbox, BadgeDollarSign, Scale, Navigation, ChevronDown, ChevronUp, Download, Plus, Pencil, Trash2, X, Check, FileText, Layers } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CLS = {
  ACCEPTED: "border-green-300 bg-green-50 text-green-700",
  "Payment Pending": "border-amber-300 bg-amber-50 text-amber-700",
  "Job Completed": "border-slate-300 bg-slate-100 text-slate-600",
  Cancelled: "border-red-300 bg-red-50 text-red-600",
};

const FILTERS = [
  ["all", "All"],
  ["upcoming", "Upcoming"],
  ["completed", "Completed"],
  ["unpaid", "Pending Pay"],
  ["cancelled", "Cancelled"],
];

const aud = (n, dp = 2) =>
  `$${(n || 0).toLocaleString("en-AU", { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return d;
  }
};

const weekStart = (d) => {
  try {
    const dt = new Date(d + "T00:00:00");
    const day = (dt.getDay() + 6) % 7; // Monday = 0
    dt.setDate(dt.getDate() - day);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  } catch {
    return "No date";
  }
};

const weekLabel = (start, end) => {
  if (!start) return "Undated";
  try {
    const s = new Date(start + "T00:00:00");
    const e = new Date((end || start) + "T00:00:00");
    const sm = s.toLocaleDateString("en-AU", { month: "short" });
    const em = e.toLocaleDateString("en-AU", { month: "short" });
    return sm === em
      ? `${s.getDate()}–${e.getDate()} ${em}`
      : `${s.getDate()} ${sm} – ${e.getDate()} ${em}`;
  } catch {
    return start;
  }
};

// Difference pill: positive = Movers Buddy short-paid us; negative = they paid extra.
const DiffPill = ({ diff, testid }) => {
  if (diff == null) return null;
  if (diff > 0.5)
    return <span className="rounded-full border border-red-300 bg-red-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-red-600" data-testid={testid}>Short {aud(diff)}</span>;
  if (diff < -0.5)
    return <span className="rounded-full border border-blue-300 bg-blue-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-blue-600" data-testid={testid}>MB +{aud(-diff)}</span>;
  return <span className="rounded-full border border-green-300 bg-green-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-green-700" data-testid={testid}>Matches</span>;
};

export const Reconcile = ({ l }) => {
  if (l.expected_payout == null) return null;
  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3" data-testid={`buddy-reconcile-${l.ccrm_id}`}>
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
          <Scale className="h-3.5 w-3.5 text-blaze" /> Payout Check
        </p>
        <DiffPill diff={l.difference} testid={`buddy-diff-${l.ccrm_id}`} />
      </div>
      <div className="mt-2 space-y-1 text-[11px] text-slate-600">
        <div className="flex justify-between">
          <span>Hourly rate × {l.worked_hours}h</span>
          <span className="font-semibold text-slate-800">{aud(l.labour_value)} @ {aud(l.calc_rate, 0)}/hr</span>
        </div>
        <div className="flex justify-between">
          <span>Our {l.calc_percent}% share</span>
          <span className="font-semibold text-slate-800">{aud(l.labour_share)}</span>
        </div>
        <div className="flex items-start justify-between gap-2">
          <span className="flex items-center gap-1">
            <Navigation className="h-3 w-3 text-blaze" /> Travel
            {l.travel_min ? ` · ${l.travel_min}min` : " · local (≤65km)"}
          </span>
          <span className="font-semibold text-slate-800">{l.travel_min ? `+${aud(l.travel_share)}` : "$0.00"}</span>
        </div>
        {l.both_far ? (
          <p className="rounded-md bg-amber-50 px-2 py-1 text-[10px] leading-snug text-amber-700" data-testid={`buddy-bothfar-${l.ccrm_id}`}>
            Pickup {l.pickup_km}km &amp; drop-off {l.drop_km}km from CBD — <b>both 65km+</b>, travel owed
          </p>
        ) : (l.pickup_km != null && l.drop_km != null) ? (
          <p className="text-[10px] text-slate-400">Pickup {l.pickup_km}km · Drop {l.drop_km}km from CBD</p>
        ) : null}
        {(l.stairs_share || 0) > 0.5 && (
          <div className="flex items-center justify-between" data-testid={`buddy-payout-stairs-${l.ccrm_id}`}>
            <span className="flex items-center gap-1"><Layers className="h-3 w-3 text-orange-600" /> Stairs / extras (full)</span>
            <span className="font-semibold text-orange-700">+{aud(l.stairs_share)}</span>
          </div>
        )}
        <div className="mt-1 flex justify-between border-t border-slate-200 pt-1">
          <span className="font-bold text-slate-900">Should be paid</span>
          <span className="font-bold text-slate-900">{aud(l.expected_payout)}</span>
        </div>
        <div className="flex justify-between">
          <span>Movers Buddy shows</span>
          <span className="font-semibold text-slate-800">{l.mb_payout != null ? aud(l.mb_payout) : "—"}</span>
        </div>
      </div>
    </div>
  );
};

// What Movers Buddy charged the CUSTOMER for this job, itemised. Stairs/extras is admin-editable.
export const CustomerCharge = ({ l, onSetExtras }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const total = Number(l.job_total);
  if (!total) return null;
  const labour = Number(l.invoice_labour_total) || 0;
  const travel = Number(l.travel_fee) || 0;
  const crmExtras = Number(l.extras_total) || 0;
  const hasOverride = l.extras_override !== undefined && l.extras_override !== null;
  const extras = hasOverride ? Number(l.extras_override) : crmExtras;
  const otherFees = Number(l.other_fees) || 0;
  const gst = Number(l.gst_amount) || 0;
  const cc = Number(l.cc_surcharge) || 0;
  const discount = Number(l.discount) || 0;
  const deposit = Number(l.deposit) || 0;
  const stairs = [l.stairs_pickup && `Pickup — ${l.stairs_pickup}`, l.stairs_drop && `Drop — ${l.stairs_drop}`].filter(Boolean).join(" · ");
  const hasStairs = !!stairs;
  const showExtras = hasStairs || crmExtras > 0 || hasOverride;
  const beginEdit = () => { setVal(hasOverride ? String(l.extras_override) : (crmExtras ? String(crmExtras) : "")); setEditing(true); };
  const save = () => {
    const n = parseFloat(val);
    if (isNaN(n) || n < 0) { toast.error("Enter a valid amount"); return; }
    onSetExtras(l.ccrm_id, n); setEditing(false);
  };
  const Row = ({ label, icon, value, strong, cls }) => (
    <div className="flex items-center justify-between">
      <span className={`flex items-center gap-1 ${cls || "text-slate-500"}`}>{icon}{label}</span>
      <span className={strong ? "font-bold text-slate-900" : `font-semibold ${cls || "text-slate-700"}`}>{value}</span>
    </div>
  );
  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3" data-testid={`buddy-charge-${l.ccrm_id}`}>
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
          <BadgeDollarSign className="h-3.5 w-3.5 text-green-600" /> MB charged customer
        </p>
        {l.payment_method && (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-500">{String(l.payment_method).replace(/_/g, " ")}</span>
        )}
      </div>
      <div className="mt-2 space-y-1 text-[11px]">
        {labour > 0 && <Row label={`Labour${l.worked_hours ? ` · ${l.worked_hours}h` : ""}`} value={aud(labour)} />}
        <Row label="Travel fee" icon={<Navigation className="h-3 w-3 text-blaze" />} value={travel > 0 ? aud(travel) : "$0.00"} />
        {showExtras && (
          <div data-testid={`buddy-charge-stairs-${l.ccrm_id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1 text-orange-600"><Layers className="h-3 w-3" />{hasStairs ? "Stairs / extras" : "Extras"}</span>
              {editing ? (
                <span className="flex items-center gap-1">
                  <span className="text-slate-400">$</span>
                  <input
                    autoFocus type="number" step="0.01" value={val}
                    onChange={(e) => setVal(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
                    className="w-16 rounded-md border border-orange-300 px-1.5 py-0.5 text-right text-[11px] focus:outline-none focus:ring-1 focus:ring-orange-400"
                    data-testid={`buddy-extras-input-${l.ccrm_id}`}
                  />
                  <button onClick={save} className="rounded-md bg-orange-500 p-1 text-white hover:bg-orange-600" data-testid={`buddy-extras-save-${l.ccrm_id}`}><Check className="h-3 w-3" /></button>
                  <button onClick={() => setEditing(false)} className="rounded-md bg-slate-200 p-1 text-slate-600 hover:bg-slate-300"><X className="h-3 w-3" /></button>
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <span className="font-bold text-orange-700">{extras > 0 ? aud(extras) : "Included"}</span>
                  <button onClick={beginEdit} className="text-orange-400 hover:text-orange-600" data-testid={`buddy-extras-edit-${l.ccrm_id}`}><Pencil className="h-3 w-3" /></button>
                </span>
              )}
            </div>
            {hasOverride && !editing && (
              <p className="pl-4 text-[10px] text-slate-400">
                MB charged {aud(crmExtras)} · you set {aud(extras)}
                <button onClick={() => onSetExtras(l.ccrm_id, null)} className="ml-1 font-semibold text-blaze hover:underline" data-testid={`buddy-extras-reset-${l.ccrm_id}`}>reset</button>
              </p>
            )}
            {hasStairs && <p className="pl-4 text-[10px] leading-snug text-orange-500/90">{stairs}</p>}
            {extras > 0 && <p className="pl-4 text-[10px] font-semibold text-green-700" data-testid={`buddy-stairs-share-${l.ccrm_id}`}>You claim this in full: {aud(extras)}</p>}
          </div>
        )}
        {otherFees > 0 && <Row label="Other fees" value={aud(otherFees)} />}
        {gst > 0 && <Row label="GST (10%)" value={aud(gst)} />}
        {cc > 0 && <Row label="Card surcharge" value={aud(cc)} />}
        {discount > 0 && <Row label="Discount" cls="text-green-600" value={`−${aud(discount)}`} />}
        <div className="mt-1 border-t border-slate-200 pt-1">
          <Row label="Total charged" value={aud(total)} strong />
        </div>
        {deposit > 0 && <Row label="Deposit paid" value={aud(deposit)} />}
      </div>
    </div>
  );
};

// Mover's signed-off hours vs what Movers Buddy invoiced.
export const HoursCompare = ({ l }) => {
  const moverH = l.signoff_hours != null ? Number(l.signoff_hours) : null;
  const mbH = l.worked_hours != null ? Number(l.worked_hours) : null;
  const perMover = l.signoff_mover_hours || {};
  const diff = moverH != null && mbH != null ? Math.round((moverH - mbH) * 100) / 100 : null;
  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3" data-testid={`buddy-hours-${l.ccrm_id}`}>
      <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500"><Clock className="h-3.5 w-3.5 text-blaze" /> Hours — mover vs Movers Buddy</p>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-blue-50 px-2 py-2">
          <p className="font-display text-base font-bold text-blue-700">{moverH != null ? `${moverH}h` : "—"}</p>
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Mover logged</p>
        </div>
        <div className="rounded-lg bg-slate-100 px-2 py-2">
          <p className="font-display text-base font-bold text-slate-700">{mbH != null ? `${mbH}h` : "—"}</p>
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">MB charged</p>
        </div>
        <div className={`rounded-lg px-2 py-2 ${diff != null && Math.abs(diff) > 0.01 ? "bg-amber-50" : "bg-slate-100"}`}>
          <p className={`font-display text-base font-bold ${diff != null && Math.abs(diff) > 0.01 ? "text-amber-600" : "text-slate-700"}`}>{diff != null ? `${diff > 0 ? "+" : ""}${diff}h` : "—"}</p>
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Difference</p>
        </div>
      </div>
      {Object.keys(perMover).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1 text-[10px]">
          {Object.entries(perMover).map(([m, h]) => (
            <span key={m} className="rounded bg-slate-100 px-2 py-0.5 text-slate-600">{m}: {h}h</span>
          ))}
        </div>
      )}
      {moverH == null && (
        <p className="mt-2 text-[10px] text-slate-400">No app sign-off linked for this job — showing Movers Buddy figures only.</p>
      )}
      {l.signoff_travel_hours != null && (
        <p className="mt-2 text-[11px] text-slate-600">Travel logged by mover: <b>{l.signoff_travel_hours}h</b></p>
      )}
      {l.signoff_notes && <p className="mt-1 text-[11px] text-slate-500">Note: {l.signoff_notes}</p>}
    </div>
  );
};

export const MoversBuddy = ({ onImported }) => {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [converting, setConverting] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [filter, setFilter] = useState("all");
  const [groupBy, setGroupBy] = useState("week");
  const [showWeekly, setShowWeekly] = useState(false);
  const [expandedWeek, setExpandedWeek] = useState(null);
  const BLANK_PAY = { editingId: null, amount: "", received_date: "", note: "", category: "Labour" };
  const [payForm, setPayForm] = useState(BLANK_PAY);
  const [savingPay, setSavingPay] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [pendingMode, setPendingMode] = useState(null); // 'pending' | 'unpaid' | null
  const [detailLead, setDetailLead] = useState(null);
  const [expPendWeek, setExpPendWeek] = useState(null);
  const [view, setView] = useState("jobs");
  const [custOpen, setCustOpen] = useState({});
  const [custSearch, setCustSearch] = useState("");
  const [jobSearch, setJobSearch] = useState("");
  const [expandedJobs, setExpandedJobs] = useState({});
  const toggleJob = (id) => setExpandedJobs((m) => ({ ...m, [id]: !m[id] }));

  const loadLeads = () => axios.get(`${API}/connectcrm/leads`).then(({ data }) => setData(data)).catch(() => setData({ summary: {}, weekly: [], leads: [] }));

  const sync = async (silent = false) => {
    setSyncing(true);
    try {
      const { data: r } = await axios.post(`${API}/connectcrm/sync`);
      if (!silent) toast.success(`Synced — ${r.fetched} job(s) from Movers Buddy${r.new ? `, ${r.new} new` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Movers Buddy sync failed");
    } finally {
      setSyncing(false);
      loadLeads();
    }
  };

  useEffect(() => {
    loadLeads();
    sync(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const convert = async (lead) => {
    setConverting(lead.ccrm_id);
    try {
      const { data: r } = await axios.post(`${API}/connectcrm/leads/${lead.ccrm_id}/convert`);
      toast.success(`Imported as job ${r.job_id} — assign movers from the Jobs tab`);
      loadLeads();
      onImported && onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setConverting(null);
    }
  };

  const unconvert = async (lead) => {
    if (!window.confirm(`Remove imported job ${lead.converted_job_id}? It will be deleted from the Jobs tab.`)) return;
    setRemoving(lead.ccrm_id);
    try {
      await axios.post(`${API}/connectcrm/leads/${lead.ccrm_id}/unconvert`);
      toast.success("Removed from Jobs");
      loadLeads();
      onImported && onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remove failed");
    } finally {
      setRemoving(null);
    }
  };

  const toggleWeek = (ws) => {
    setExpandedWeek((cur) => (cur === ws ? null : ws));
    setPayForm(BLANK_PAY);
  };

  const savePayment = async (weekStartIso) => {
    const amt = parseFloat(payForm.amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setSavingPay(true);
    const payload = { week_start: weekStartIso, amount: amt, received_date: payForm.received_date, note: payForm.note, category: payForm.category || "Labour" };
    try {
      if (payForm.editingId) {
        await axios.put(`${API}/connectcrm/payments/${payForm.editingId}`, payload);
        toast.success("Payment updated");
      } else {
        await axios.post(`${API}/connectcrm/payments`, payload);
        toast.success("Payment added");
      }
      setPayForm(BLANK_PAY);
      loadLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save payment");
    } finally {
      setSavingPay(false);
    }
  };

  const deletePayment = async (id) => {
    if (!window.confirm("Delete this received payment?")) return;
    try {
      await axios.delete(`${API}/connectcrm/payments/${id}`);
      toast.success("Payment deleted");
      loadLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const markTravelPaid = async (ccrmId, paid) => {
    try {
      await axios.post(`${API}/connectcrm/leads/${ccrmId}/travel-paid`, { paid });
      toast.success(paid ? "Travel marked paid" : "Travel marked unpaid");
      loadLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const markStairsPaid = async (ccrmId, paid) => {
    try {
      await axios.post(`${API}/connectcrm/leads/${ccrmId}/stairs-paid`, { paid });
      toast.success(paid ? "Stairs marked paid" : "Stairs marked unpaid");
      loadLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const markAllPaid = async (kind, paid) => {
    const field = kind === "travel" ? "travel_paid" : "stairs_paid";
    const shareField = kind === "travel" ? "travel_share" : "stairs_share";
    const targets = diffJobs.filter((l) => (l[shareField] || 0) > 0.5 && !!l[field] !== paid);
    if (!targets.length) return;
    try {
      await Promise.all(targets.map((l) => axios.post(`${API}/connectcrm/leads/${l.ccrm_id}/${kind}-paid`, { paid })));
      toast.success(`${kind === "travel" ? "Travel" : "Stairs"} ${paid ? "marked paid" : "marked unpaid"}`);
      loadLeads();
    } catch (e) {
      toast.error("Could not update");
    }
  };

  const markWeekTravelPaid = async (weekStart, paid) => {
    try {
      await axios.post(`${API}/connectcrm/week-travel-paid`, { week_start: weekStart, paid });
      toast.success(paid ? "Week travel marked paid" : "Week travel marked unpaid");
      loadLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const markWeekStairsPaid = async (weekStart, paid) => {
    try {
      await axios.post(`${API}/connectcrm/week-stairs-paid`, { week_start: weekStart, paid });
      toast.success(paid ? "Week stairs marked paid" : "Week stairs marked unpaid");
      loadLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const setExtras = async (ccrmId, amount) => {
    try {
      await axios.post(`${API}/connectcrm/leads/${ccrmId}/extras`, { amount });
      toast.success(amount == null ? "Reset to Movers Buddy amount" : "Stairs / extras updated");
      loadLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const leads = data?.leads || [];
  const s = data?.summary || {};
  const weekly = data?.weekly || [];

  const filtered = useMemo(() => {
    let list = leads;
    if (filter === "upcoming") list = leads.filter((l) => l.job_status !== "Job Completed" && l.job_status !== "Cancelled");
    else if (filter === "completed") list = leads.filter((l) => l.job_status === "Job Completed");
    else if (filter === "unpaid") list = leads.filter((l) => l.job_status === "Job Completed" && l.payout_status !== "Paid");
    else if (filter === "cancelled") list = leads.filter((l) => l.job_status === "Cancelled");
    const q = jobSearch.trim().toLowerCase();
    if (q) list = list.filter((l) => String(l.ccrm_job_number || "").toLowerCase().includes(q) || `${l.first_name} ${l.last_name}`.toLowerCase().includes(q) || (l.converted_job_id || "").toLowerCase().includes(q));
    return list;
  }, [leads, filter, jobSearch]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((l) => {
      const k = groupBy === "week" ? (l.moving_date ? weekStart(l.moving_date) : "No date") : (l.moving_date || "No date");
      (g[k] = g[k] || []).push(l);
    });
    return Object.entries(g).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filtered, groupBy]);

  const diffJobs = useMemo(
    () => leads.filter((l) => (l.difference || 0) > 0.5).sort((a, b) => (b.difference || 0) - (a.difference || 0)),
    [leads]
  );

  const counts = useMemo(() => ({
    cancelled: leads.filter((l) => l.job_status === "Cancelled").length,
    unpaid: leads.filter((l) => l.job_status === "Job Completed" && l.payout_status !== "Paid").length,
  }), [leads]);

  const customers = useMemo(() => {
    const map = {};
    leads.forEach((l) => {
      const key = (l.phone || l.email || `${l.first_name} ${l.last_name}`).trim().toLowerCase();
      if (!map[key]) {
        map[key] = { key, name: `${l.first_name || ""} ${l.last_name || ""}`.trim() || "Unknown", phone: l.phone || "", email: l.email || "", jobs: [], mb_total: 0 };
      }
      map[key].jobs.push(l);
      map[key].mb_total += l.mb_payout || 0;
    });
    let list = Object.values(map);
    const q = custSearch.trim().toLowerCase();
    if (q) list = list.filter((c) => c.name.toLowerCase().includes(q) || c.phone.toLowerCase().includes(q) || c.email.toLowerCase().includes(q));
    list.forEach((c) => c.jobs.sort((a, b) => (b.moving_date || "").localeCompare(a.moving_date || "")));
    return list.sort((a, b) => b.jobs.length - a.jobs.length || a.name.localeCompare(b.name));
  }, [leads, custSearch]);

  const toggleCust = (k) => setCustOpen((m) => ({ ...m, [k]: !m[k] }));

  if (data === null) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blaze" /></div>;
  }

  return (
    <div className="mt-6 pb-10" data-testid="movers-buddy-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blaze">Movers Buddy — Full Platform</p>
          <p className="mt-1 text-xs text-slate-500">All jobs from platform.connectcrm.com.au — 63% contract reconciliation with travel.</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${API}/connectcrm/statement.csv`}
            className="flex items-center gap-1.5 rounded border border-line bg-white px-3 py-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-700 active:scale-95"
            data-testid="buddy-export-csv"
          >
            <Download className="h-3.5 w-3.5" /> CSV
          </a>
          <a
            href={`${API}/connectcrm/statement.pdf`}
            className="flex items-center gap-1.5 rounded border border-line bg-white px-3 py-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-700 active:scale-95"
            data-testid="buddy-export-pdf"
          >
            <FileText className="h-3.5 w-3.5" /> PDF
          </a>
          <button
            onClick={() => sync(false)}
            disabled={syncing}
            className="flex items-center gap-2 bg-blaze px-5 py-2.5 text-[10px] font-bold uppercase tracking-widest text-white active:scale-95 disabled:opacity-50"
            data-testid="buddy-sync-btn"
          >
            {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />} Sync Now
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4" data-testid="buddy-summary">
        {[
          { icon: Inbox, label: "Total Jobs", val: s.total || 0 },
          { icon: CalendarDays, label: "Upcoming", val: s.upcoming || 0 },
          { icon: CheckCircle2, label: "Completed", val: s.completed || 0 },
          { icon: BadgeDollarSign, label: "MB Paid", val: aud(s.earnings || 0, 0) },
          { icon: Scale, label: "Should Be", val: aud(s.expected_earnings || 0, 0) },
          {
            icon: Wallet, label: "Difference", testid: "buddy-difference-card",
            val: `${(s.difference_outstanding || 0) > 0 ? "+" : ""}${aud(s.difference_outstanding || 0, 0)}`,
            onClick: () => setShowDiff(true), hot: (s.difference_outstanding || 0) > 0.5,
          },
          {
            icon: Clock, label: "Pending", testid: "buddy-pending-card",
            val: aud(s.pending_total || 0, 0), onClick: () => setPendingMode("pending"),
            hot: (s.pending_total || 0) > 0.5, amber: true,
          },
          {
            icon: Wallet, label: "MB Unpaid", testid: "buddy-mb-unpaid-card",
            val: aud(s.unpaid_amount || 0, 0), onClick: () => setPendingMode("unpaid"),
            hot: (s.unpaid_amount || 0) > 0.5, amber: true,
          },
        ].map((c, i) => {
          const clickable = !!c.onClick;
          const accent = c.hot ? (c.amber ? "text-amber-500" : "text-red-500") : "text-blaze";
          const numCls = c.hot ? (c.amber ? "text-amber-600" : "text-red-600") : "text-slate-900";
          const border = c.hot ? (c.amber ? "border-amber-200" : "border-red-200") : "border-slate-200/80";
          return (
            <div
              key={c.label}
              onClick={c.onClick}
              role={clickable ? "button" : undefined}
              className={`rounded-2xl border bg-white p-3.5 shadow-[0_8px_30px_rgb(0,0,0,0.05)] ${border} ${clickable ? "cursor-pointer transition-shadow hover:shadow-[0_10px_34px_rgb(0,0,0,0.1)]" : ""}`}
              data-testid={c.testid || `buddy-card-${i}`}
            >
              <c.icon className={`h-4 w-4 ${accent}`} />
              <p className={`mt-1.5 font-display text-xl font-bold ${numCls}`}>{c.val}</p>
              <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                {c.label}{clickable && <span className="text-blaze">· view</span>}
              </p>
            </div>
          );
        })}
      </div>
      {(s.unpaid || 0) > 0 && (
        <p className="mt-2 inline-block rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-[11px] font-bold text-amber-700" data-testid="buddy-unpaid-banner">
          <Wallet className="mr-1.5 inline h-3.5 w-3.5" />{s.unpaid} completed job{s.unpaid === 1 ? "" : "s"} still unpaid by Movers Buddy
        </p>
      )}

      <div className="mt-4 flex overflow-hidden rounded-full border border-line" data-testid="buddy-view-toggle">
        {[["jobs", "Jobs"], ["customers", `Customers (${leads.length ? customers.length : 0})`]].map(([k, lbl]) => (
          <button
            key={k}
            onClick={() => setView(k)}
            className={`px-5 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors ${view === k ? "bg-blaze text-white" : "bg-white text-slate-600"}`}
            data-testid={`buddy-view-${k}`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {view === "customers" && (
        <div className="mt-4" data-testid="buddy-customers">
          <input
            type="text"
            value={custSearch}
            onChange={(e) => setCustSearch(e.target.value)}
            placeholder="Search customer by name, phone or email…"
            className="w-full rounded-xl border border-line px-4 py-2.5 text-sm outline-none focus:border-blaze"
            data-testid="buddy-customer-search"
          />
          <p className="mt-2 text-[11px] font-semibold text-slate-400">{customers.length} customer{customers.length === 1 ? "" : "s"} in the Movers Buddy CRM</p>
          <div className="mt-3 space-y-2.5">
            {customers.map((cst) => {
              const open = !!custOpen[cst.key];
              return (
                <div key={cst.key} className="rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid={`buddy-customer-${cst.key}`}>
                  <button onClick={() => toggleCust(cst.key)} className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left" data-testid={`buddy-customer-toggle-${cst.key}`}>
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50 font-display text-sm font-bold text-blaze">{(cst.name || "?").slice(0, 2).toUpperCase()}</span>
                      <div className="min-w-0">
                        <p className="font-display text-base font-bold text-slate-900">{cst.name}</p>
                        <p className="truncate text-xs text-slate-500">{cst.phone || cst.email || "—"}</p>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-600">{cst.jobs.length} job{cst.jobs.length === 1 ? "" : "s"}</span>
                      {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                    </div>
                  </button>
                  {open && (
                    <div className="border-t border-slate-100 px-4 py-3" data-testid={`buddy-customer-jobs-${cst.key}`}>
                      <div className="mb-2 flex flex-wrap gap-2 text-[11px]">
                        {cst.phone && <a href={`tel:${cst.phone}`} className="flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 font-bold text-white"><Phone className="h-3 w-3" /> Call</a>}
                        <span className="rounded-lg bg-slate-100 px-3 py-1.5 font-semibold text-slate-600">MB total {aud(cst.mb_total)}</span>
                        {cst.email && <span className="rounded-lg bg-slate-100 px-3 py-1.5 text-slate-500">{cst.email}</span>}
                      </div>
                      <div className="space-y-2">
                        {cst.jobs.map((j) => (
                          <div key={j.ccrm_id} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid={`buddy-customer-job-${j.ccrm_id}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-bold text-blaze">MB #{j.ccrm_job_number}</p>
                                <p className="text-[11px] text-slate-500"><CalendarDays className="mr-1 inline h-3 w-3 text-blaze" />{fmtDate(j.moving_date)}{j.start_time ? ` · ${j.start_time}` : ""}</p>
                              </div>
                              <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_CLS[j.job_status] || "border-slate-300 bg-slate-50 text-slate-600"}`}>{j.job_status || "New"}</span>
                            </div>
                            <p className="mt-1.5 text-[11px] text-slate-500">
                              <MapPin className="mr-1 inline h-3 w-3 text-green-600" />{j.pickup_address || j.pickup_suburb || "—"}
                              <span className="mx-1 text-slate-300">→</span>
                              <MapPin className="mr-1 inline h-3 w-3 text-red-500" />{j.drop_address || j.drop_suburb || "—"}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "jobs" && (
      <>
      <input
        type="text"
        value={jobSearch}
        onChange={(e) => setJobSearch(e.target.value)}
        placeholder="Search by Job ID or customer name…"
        className="mt-4 w-full rounded-xl border border-line px-4 py-2.5 text-sm outline-none focus:border-blaze"
        data-testid="buddy-job-search"
      />
      {/* Weekly reconciliation */}
      {weekly.length > 0 && (
        <div className="mt-5 rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid="buddy-weekly">
          <button
            onClick={() => setShowWeekly((v) => !v)}
            className="flex w-full items-center justify-between px-4 py-3"
            data-testid="buddy-weekly-toggle"
          >
            <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-700">
              <CalendarDays className="h-4 w-4 text-blaze" /> Weekly Reconciliation
            </span>
            {showWeekly ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </button>
          {showWeekly && (
            <div className="overflow-x-auto border-t border-slate-100">
              <table className="w-full min-w-[640px] text-left text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                    <th className="px-4 py-2 font-bold">Week</th>
                    <th className="px-3 py-2 text-center font-bold">Jobs</th>
                    <th className="px-3 py-2 text-right font-bold">MB App</th>
                    <th className="px-3 py-2 text-right font-bold">Should Be</th>
                    <th className="px-3 py-2 text-right font-bold">Rate Diff</th>
                    <th className="px-3 py-2 text-right font-bold">Received</th>
                    <th className="px-3 py-2 text-right font-bold">Pending</th>
                    <th className="px-3 py-2 text-center font-bold"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {weekly.map((w) => {
                    const open = expandedWeek === w.week_start;
                    return (
                      <Fragment key={w.week_start}>
                        <tr
                          onClick={() => toggleWeek(w.week_start)}
                          className="cursor-pointer hover:bg-slate-50"
                          data-testid={`buddy-week-${w.week_start}`}
                        >
                          <td className="px-4 py-2.5 font-semibold text-slate-800">{weekLabel(w.week_start, w.week_end)}</td>
                          <td className="px-3 py-2.5 text-center text-slate-600">{w.count}</td>
                          <td className="px-3 py-2.5 text-right text-slate-700">{aud(w.mb_total)}</td>
                          <td className="px-3 py-2.5 text-right font-semibold text-slate-900">{aud(w.expected_total)}</td>
                          <td className={`px-3 py-2.5 text-right font-bold ${w.rate_diff > 0.5 ? "text-red-600" : w.rate_diff < -0.5 ? "text-blue-600" : "text-green-700"}`}>
                            {w.rate_diff > 0 ? "+" : ""}{aud(w.rate_diff)}
                          </td>
                          <td className="px-3 py-2.5 text-right text-green-700">{w.received_total ? aud(w.received_total) : "—"}</td>
                          <td className={`px-3 py-2.5 text-right font-semibold ${w.pending > 0.5 ? "text-amber-600" : "text-slate-400"}`}>{aud(w.pending)}</td>
                          <td className="px-3 py-2.5 text-center text-slate-400">{open ? <ChevronUp className="inline h-3.5 w-3.5" /> : <ChevronDown className="inline h-3.5 w-3.5" />}</td>
                        </tr>
                        {open && (
                          <tr data-testid={`buddy-week-detail-${w.week_start}`}>
                            <td colSpan={8} className="bg-slate-50/70 px-4 py-4">
                              {/* Difference breakdown */}
                              <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">Week summary</p>
                              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
                                {[
                                  ["MB App total", aud(w.mb_total), "text-slate-800"],
                                  ["Contract 63%", aud(w.expected_total), "text-slate-800"],
                                  ["Total diff", `${w.difference_total > 0 ? "+" : ""}${aud(w.difference_total)}`, w.difference_total > 0.5 ? "text-red-600" : w.difference_total < -0.5 ? "text-blue-600" : "text-green-700"],
                                  ["Diff pending", aud(w.diff_pending), w.diff_pending > 0.5 ? "text-amber-600" : "text-slate-400"],
                                  ["Travel owed", w.travel_total ? aud(w.travel_total) : "$0.00", "text-slate-800"],
                                  ["MB marked paid", aud(w.paid_total), "text-green-700"],
                                  ["MB unpaid", aud(w.unpaid_total), w.unpaid_total > 0.5 ? "text-red-600" : "text-slate-400"],
                                  ["Pending to me", aud(w.pending), w.pending > 0.5 ? "text-amber-600" : "text-slate-400"],
                                ].map(([lbl, val, cls]) => (
                                  <div key={lbl} className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                                    <p className={`font-display text-sm font-bold ${cls}`}>{val}</p>
                                    <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{lbl}</p>
                                  </div>
                                ))}
                              </div>

                              {/* Where the difference comes from: travel / stairs / rate */}
                              {(w.travel_owed > 0.5 || w.stairs_owed > 0.5 || w.rate_owed > 0.5 || (w.stairs_jobs || []).length > 0) && (
                                <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3" data-testid={`buddy-reclaim-${w.week_start}`}>
                                  <p className="mb-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">Where the difference comes from</p>
                                  <div className="space-y-2.5 text-[11px]">
                                    {w.travel_owed > 0.5 && (
                                      <div data-testid={`buddy-src-travel-${w.week_start}`}>
                                        <div className="flex items-center justify-between">
                                          <span className="flex items-center gap-1 font-bold text-slate-700"><Navigation className="h-3 w-3 text-blaze" />Travel</span>
                                          <span className="text-slate-500">owed <b className="text-slate-800">{aud(w.travel_owed)}</b></span>
                                        </div>
                                        <div className="mt-1 flex items-center justify-between">
                                          <span><span className="text-green-700">Paid {aud(w.travel_paid)}</span><span className="text-slate-300"> · </span><span className={w.travel_unpaid > 0.5 ? "font-bold text-red-600" : "text-slate-400"} data-testid={`buddy-travel-unpaid-${w.week_start}`}>Pending {aud(w.travel_unpaid)}</span></span>
                                          <button
                                            onClick={() => markWeekTravelPaid(w.week_start, w.travel_unpaid > 0.5)}
                                            className={`rounded-md px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider active:scale-95 ${w.travel_unpaid > 0.5 ? "bg-green-600 text-white" : "border border-slate-300 bg-white text-slate-500"}`}
                                            data-testid={`buddy-week-travel-toggle-${w.week_start}`}
                                          >
                                            {w.travel_unpaid > 0.5 ? "Mark paid" : "Paid ✓"}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                    {(w.stairs_owed > 0.5 || (w.stairs_jobs || []).length > 0) && (
                                      <div className="border-t border-slate-100 pt-2" data-testid={`buddy-src-stairs-${w.week_start}`}>
                                        <div className="flex items-center justify-between">
                                          <span className="flex items-center gap-1 font-bold text-orange-600"><Layers className="h-3 w-3" />Stairs / extras <span className="font-normal text-slate-400">(full)</span></span>
                                          <span className="text-slate-500">owed <b className="text-orange-700">{aud(w.stairs_owed)}</b></span>
                                        </div>
                                        <div className="mt-1 flex items-center justify-between">
                                          <span><span className="text-green-700">Paid {aud(w.stairs_paid)}</span><span className="text-slate-300"> · </span><span className={w.stairs_unpaid > 0.5 ? "font-bold text-red-600" : "text-slate-400"} data-testid={`buddy-stairs-unpaid-${w.week_start}`}>Pending {aud(w.stairs_unpaid)}</span></span>
                                          {w.stairs_owed > 0.5 && (
                                            <button
                                              onClick={() => markWeekStairsPaid(w.week_start, w.stairs_unpaid > 0.5)}
                                              className={`rounded-md px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider active:scale-95 ${w.stairs_unpaid > 0.5 ? "bg-green-600 text-white" : "border border-slate-300 bg-white text-slate-500"}`}
                                              data-testid={`buddy-week-stairs-toggle-${w.week_start}`}
                                            >
                                              {w.stairs_unpaid > 0.5 ? "Mark paid" : "Paid ✓"}
                                            </button>
                                          )}
                                        </div>
                                        <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`buddy-week-stairs-${w.week_start}`}>
                                          {(w.stairs_jobs || []).map((sj) => (
                                            <span key={sj.job} className={`rounded px-2 py-0.5 text-[10px] ${sj.paid ? "bg-green-50 text-green-700" : sj.owed > 0.5 ? "bg-orange-50 text-orange-700" : "bg-slate-100 text-slate-500"}`}>#{sj.job} — {sj.owed > 0.5 ? aud(sj.owed) : "set $"}{sj.stairs ? ` · ${sj.stairs}` : ""}</span>
                                          ))}
                                        </div>
                                        {(w.stairs_jobs || []).some((sj) => sj.owed <= 0.5) && (
                                          <p className="mt-1 text-[10px] text-slate-400">Jobs showing “set $” have stairs but no amount from Movers Buddy — open the job above to enter what they paid.</p>
                                        )}
                                      </div>
                                    )}
                                    {w.rate_owed > 0.5 && (
                                      <div className="flex items-center justify-between border-t border-slate-100 pt-2" data-testid={`buddy-src-rate-${w.week_start}`}>
                                        <span className="flex items-center gap-1 font-bold text-slate-700"><Scale className="h-3 w-3 text-blue-500" />Rate difference</span>
                                        <span className="text-slate-500">owed <b className="text-blue-600">{aud(w.rate_owed)}</b><span className="text-slate-300"> · </span><span className="font-bold text-red-600">Pending {aud(w.rate_owed)}</span></span>
                                      </div>
                                    )}
                                    <div className="flex items-center justify-between border-t border-slate-200 pt-2">
                                      <span className="font-bold uppercase tracking-wider text-slate-500">Total pending</span>
                                      <span className="font-display text-sm font-bold text-amber-600" data-testid={`buddy-diff-pending-${w.week_start}`}>{aud(w.diff_pending)}</span>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Received payments */}
                              <p className="mb-2 mt-4 text-[10px] font-bold uppercase tracking-widest text-slate-500">Payments received this week</p>
                              <div className="space-y-1.5" data-testid={`buddy-payments-${w.week_start}`}>
                                {(w.payments || []).length === 0 && (
                                  <p className="text-[11px] text-slate-400">No payments recorded yet.</p>
                                )}
                                {(w.payments || []).map((p) => (
                                  <div key={p.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px]" data-testid={`buddy-payment-${p.id}`}>
                                    <div className="min-w-0">
                                      <span className="font-bold text-slate-900">{aud(p.amount)}</span>
                                      <span className="ml-1.5 rounded bg-blue-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-blaze">{p.category || "Labour"}</span>
                                      <span className="text-slate-400"> · {p.received_date || "—"}</span>
                                      {p.note && <span className="text-slate-500"> · {p.note}</span>}
                                    </div>
                                    <div className="flex shrink-0 gap-1">
                                      <button onClick={() => setPayForm({ editingId: p.id, amount: String(p.amount), received_date: p.received_date || "", note: p.note || "", category: p.category || "Labour" })} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" data-testid={`buddy-payment-edit-${p.id}`}>
                                        <Pencil className="h-3.5 w-3.5" />
                                      </button>
                                      <button onClick={() => deletePayment(p.id)} className="rounded p-1.5 text-red-500 hover:bg-red-50" data-testid={`buddy-payment-delete-${p.id}`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {Object.keys(w.received_by_cat || {}).length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`buddy-received-cats-${w.week_start}`}>
                                  {Object.entries(w.received_by_cat).map(([cat, amt]) => (
                                    <span key={cat} className="rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-[10px] font-bold text-green-700">
                                      {cat}: {aud(amt)}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {/* Add / edit form */}
                              <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white p-2.5">
                                <input
                                  type="number"
                                  step="0.01"
                                  placeholder="Amount $"
                                  value={payForm.amount}
                                  onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))}
                                  className="w-24 rounded border border-line px-2.5 py-1.5 text-xs outline-none focus:border-blaze"
                                  data-testid={`buddy-pay-amount-${w.week_start}`}
                                />
                                <select
                                  value={payForm.category}
                                  onChange={(e) => setPayForm((f) => ({ ...f, category: e.target.value }))}
                                  className="rounded border border-line px-2 py-1.5 text-xs outline-none focus:border-blaze"
                                  data-testid={`buddy-pay-category-${w.week_start}`}
                                >
                                  {["Labour", "Travel", "Stairs", "Other"].map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <input
                                  type="date"
                                  value={payForm.received_date}
                                  onChange={(e) => setPayForm((f) => ({ ...f, received_date: e.target.value }))}
                                  className="rounded border border-line px-2.5 py-1.5 text-xs outline-none focus:border-blaze"
                                  data-testid={`buddy-pay-date-${w.week_start}`}
                                />
                                <input
                                  type="text"
                                  placeholder="Note (optional)"
                                  value={payForm.note}
                                  onChange={(e) => setPayForm((f) => ({ ...f, note: e.target.value }))}
                                  className="min-w-[120px] flex-1 rounded border border-line px-2.5 py-1.5 text-xs outline-none focus:border-blaze"
                                  data-testid={`buddy-pay-note-${w.week_start}`}
                                />
                                <button
                                  onClick={() => savePayment(w.week_start)}
                                  disabled={savingPay}
                                  className="flex items-center gap-1.5 rounded bg-blaze px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white active:scale-95 disabled:opacity-50"
                                  data-testid={`buddy-pay-save-${w.week_start}`}
                                >
                                  {savingPay ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : payForm.editingId ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                                  {payForm.editingId ? "Save" : "Add"}
                                </button>
                                {payForm.editingId && (
                                  <button onClick={() => setPayForm(BLANK_PAY)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100" data-testid="buddy-pay-cancel">
                                    <X className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200 bg-slate-50 text-[11px] font-bold" data-testid="buddy-weekly-totals">
                    <td className="px-4 py-2.5 text-slate-900">Totals</td>
                    <td className="px-3 py-2.5 text-center text-slate-600">{s.completed || 0}</td>
                    <td className="px-3 py-2.5 text-right text-slate-800">{aud(s.earnings || 0)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-900">{aud(s.expected_earnings || 0)}</td>
                    <td className={`px-3 py-2.5 text-right ${(s.difference_total || 0) > 0.5 ? "text-red-600" : "text-green-700"}`}>
                      {(s.difference_total || 0) > 0 ? "+" : ""}{aud(s.difference_total || 0)}
                    </td>
                    <td className="px-3 py-2.5 text-right text-green-700">{aud(s.received_total || 0)}</td>
                    <td className="px-3 py-2.5 text-right text-amber-600">{aud(s.pending_total || 0)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {FILTERS.map(([k, l]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`rounded-full border px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${filter === k ? "border-blaze bg-blaze text-white" : k === "cancelled" ? "border-line bg-white text-rose-600" : "border-line bg-white text-slate-600"}`}
            data-testid={`buddy-filter-${k}`}
          >
            {l}{k === "unpaid" && counts.unpaid ? ` (${counts.unpaid})` : ""}{k === "cancelled" && counts.cancelled ? ` (${counts.cancelled})` : ""}
          </button>
        ))}
        <span className="ml-auto flex overflow-hidden rounded-full border border-line" data-testid="buddy-groupby">
          {[["week", "By Week"], ["date", "By Day"]].map(([k, lbl]) => (
            <button
              key={k}
              onClick={() => setGroupBy(k)}
              className={`px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${groupBy === k ? "bg-ink text-white" : "bg-white text-slate-500"}`}
              data-testid={`buddy-groupby-${k}`}
            >
              {lbl}
            </button>
          ))}
        </span>
      </div>

      {grouped.length === 0 ? (
        <p className="mt-8 border border-line/60 p-8 text-center text-sm text-slate-500" data-testid="buddy-empty">No jobs in this view.</p>
      ) : (
        grouped.map(([key, items]) => {
          const label = groupBy === "week" && key !== "No date"
            ? `Week of ${weekLabel(key, weekStart(key) && (() => { const e = new Date(new Date(key + "T00:00:00").getTime() + 6 * 864e5); return `${e.getFullYear()}-${String(e.getMonth() + 1).padStart(2, "0")}-${String(e.getDate()).padStart(2, "0")}`; })())}`
            : fmtDate(key);
          const mbSum = items.reduce((a, l) => a + (l.mb_payout || 0), 0);
          const expSum = items.reduce((a, l) => a + (l.expected_payout || 0), 0);
          const diffSum = items.reduce((a, l) => a + (l.difference || 0), 0);
          return (
            <div key={key} className="mt-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                  <CalendarDays className="h-3.5 w-3.5 text-blaze" /> {label} ({items.length})
                </p>
                {expSum > 0 && (
                  <p className="text-[11px] font-semibold text-slate-500">
                    MB {aud(mbSum, 0)} · Should {aud(expSum, 0)} ·{" "}
                    <span className={diffSum > 0.5 ? "text-red-600" : diffSum < -0.5 ? "text-blue-600" : "text-green-700"}>
                      {diffSum > 0 ? "+" : ""}{aud(diffSum, 0)}
                    </span>
                  </p>
                )}
              </div>
              <div className="mt-2.5 grid items-start gap-3 md:grid-cols-2" data-testid={`buddy-group-${key}`}>
                {items.map((l) => {
                  const isCompleted = l.job_status === "Job Completed";
                  const isOpen = !isCompleted || !!expandedJobs[l.ccrm_id];
                  return (
                  <div key={l.ccrm_id} className={`rounded-2xl border bg-white shadow-[0_8px_30px_rgb(0,0,0,0.06)] ${l.short_paid && !l.travel_paid ? "border-red-300 ring-1 ring-red-200" : "border-slate-200/80"}`} data-testid={`buddy-lead-${l.ccrm_id}`}>
                    {isCompleted ? (
                      <button
                        onClick={() => toggleJob(l.ccrm_id)}
                        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
                        data-testid={`buddy-job-toggle-${l.ccrm_id}`}
                      >
                        <div className="min-w-0">
                          <p className="font-display text-base font-bold text-blaze" data-testid={`buddy-jobno-${l.ccrm_id}`}>MB #{l.ccrm_job_number}</p>
                          <p className="truncate text-xs text-slate-500">{l.first_name} {l.last_name} · {fmtDate(l.moving_date)}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5">
                          {l.short_paid && (
                            <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${l.travel_paid ? "border-green-300 bg-green-600 text-white" : "border-red-300 bg-red-600 text-white"}`} data-testid={`buddy-shortpay-${l.ccrm_id}`}>
                              {l.travel_paid ? "Travel paid" : `Short ${aud(l.difference)}${l.short_reason ? ` · ${l.short_reason}` : ""}`}
                            </span>
                          )}
                          <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${l.payout_status === "Paid" ? "border-green-300 bg-green-50 text-green-700" : "border-red-300 bg-red-50 text-red-600"}`} data-testid={`buddy-payout-${l.ccrm_id}`}>
                            {l.payout_status || "Unpaid"}
                          </span>
                          {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                        </div>
                      </button>
                    ) : (
                      <div className="flex items-start justify-between gap-2 p-4 pb-0">
                        <div>
                          <p className="font-display text-base font-bold text-blaze" data-testid={`buddy-jobno-${l.ccrm_id}`}>MB #{l.ccrm_job_number}</p>
                          <p className="mt-0.5 text-sm font-semibold text-slate-900">{l.first_name} {l.last_name}</p>
                        </div>
                        <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_CLS[l.job_status] || "border-slate-300 bg-slate-50 text-slate-600"}`}>
                          {l.job_status || "New"}
                        </span>
                      </div>
                    )}
                    {isOpen && (
                    <div className={isCompleted ? "px-4 pb-4" : "p-4 pt-3"}>
                    <div className="grid grid-cols-2 gap-1.5 text-xs text-slate-600">
                      <p className="rounded-lg bg-slate-50 px-2.5 py-2"><Clock className="mr-1.5 inline h-3.5 w-3.5 text-blaze" />{l.start_time || l.slot || "—"}{l.worked_hours ? ` · ${l.worked_hours}h worked` : ""}</p>
                      <p className="rounded-lg bg-slate-50 px-2.5 py-2"><Truck className="mr-1.5 inline h-3.5 w-3.5 text-blaze" />{l.bedrooms ? `${l.bedrooms} Bed · ` : ""}{l.movers} men · ${l.rate}{l.pricing_type === "Hourly" ? "/hr" : ""}</p>
                      <p className="col-span-2 rounded-lg bg-slate-50 px-2.5 py-2"><MapPin className="mr-1.5 inline h-3.5 w-3.5 text-green-600" />{l.pickup_address || l.pickup_suburb || "—"}</p>
                      <p className="col-span-2 rounded-lg bg-slate-50 px-2.5 py-2"><MapPin className="mr-1.5 inline h-3.5 w-3.5 text-red-500" />{l.drop_address || l.drop_suburb || "—"}</p>
                      {(l.stairs_pickup || l.stairs_drop) && (
                        <p className="col-span-2 rounded-lg bg-orange-50 px-2.5 py-2 text-orange-700" data-testid={`buddy-stairs-${l.ccrm_id}`}>
                          <Layers className="mr-1.5 inline h-3.5 w-3.5" />
                          Stairs:{l.stairs_pickup ? ` Pickup — ${l.stairs_pickup}` : ""}{l.stairs_pickup && l.stairs_drop ? " · " : ""}{l.stairs_drop ? ` Drop — ${l.stairs_drop}` : ""}
                        </p>
                      )}
                      {l.signoff_stairs && (
                        <p className="col-span-2 rounded-lg bg-blue-50 px-2.5 py-2 font-semibold text-blaze" data-testid={`buddy-signoff-stairs-${l.ccrm_id}`}>
                          <Layers className="mr-1.5 inline h-3.5 w-3.5" />
                          Mover sign-off stairs: {l.signoff_stairs}
                        </p>
                      )}
                    </div>

                    <HoursCompare l={l} />

                    <Reconcile l={l} />

                    <CustomerCharge l={l} onSetExtras={setExtras} />

                    <button
                      onClick={() => setDetailLead(l)}
                      className="mt-3 w-full rounded-lg border border-blaze/30 bg-blaze/5 py-2 text-[11px] font-bold uppercase tracking-wider text-blaze active:scale-[0.98]"
                      data-testid={`buddy-detail-btn-${l.ccrm_id}`}
                    >
                      View full breakdown
                    </button>

                    {l.converted_job_id && (
                      <p className="mt-2 rounded-lg bg-blue-50 px-2.5 py-2 text-[11px] font-bold text-blaze" data-testid={`buddy-assigned-${l.ccrm_id}`}>
                        <Users className="mr-1.5 inline h-3.5 w-3.5" />
                        {l.converted_job_id}: {(l.our_assigned || []).length ? `Assigned to ${l.our_assigned.join(", ")}` : "Not assigned yet — open Jobs tab"}
                      </p>
                    )}
                    {l.notes && <p className="mt-2 rounded-lg bg-amber-50 px-2.5 py-2 text-[11px] leading-snug text-amber-800 line-clamp-3">{l.notes}</p>}
                    <div className="mt-3 flex gap-2">
                      {l.phone && (
                        <a href={`tel:${l.phone}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-green-600 py-2.5 text-[10px] font-bold uppercase tracking-wider text-white active:scale-95" data-testid={`buddy-call-${l.ccrm_id}`}>
                          <Phone className="h-3.5 w-3.5" /> Call
                        </a>
                      )}
                      {l.converted_job_id ? (
                        <>
                          <span className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-green-300 bg-green-50 py-2.5 text-[10px] font-bold uppercase tracking-wider text-green-700" data-testid={`buddy-imported-${l.ccrm_id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5" /> Imported · {l.converted_job_id}
                          </span>
                          <button
                            onClick={() => unconvert(l)}
                            disabled={removing === l.ccrm_id}
                            className="flex items-center justify-center gap-1.5 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider text-red-600 active:scale-95 disabled:opacity-50"
                            data-testid={`buddy-remove-${l.ccrm_id}`}
                          >
                            {removing === l.ccrm_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Trash2 className="h-3.5 w-3.5" /> Remove</>}
                          </button>
                        </>
                      ) : l.job_status !== "Job Completed" && l.job_status !== "Cancelled" ? (
                        <button
                          onClick={() => convert(l)}
                          disabled={converting === l.ccrm_id}
                          className="flex flex-1 items-center justify-center rounded-lg bg-blaze py-2.5 text-[10px] font-bold uppercase tracking-wider text-white active:scale-95 disabled:opacity-50"
                          data-testid={`buddy-convert-${l.ccrm_id}`}
                        >
                          {converting === l.ccrm_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add to Jobs"}
                        </button>
                      ) : null}
                    </div>
                    </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
      </>
      )}

      {/* Difference breakdown dialog */}
      <Dialog open={showDiff} onOpenChange={setShowDiff}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-hidden p-0" data-testid="buddy-diff-modal">
          <DialogHeader className="border-b border-slate-100 px-5 pb-3 pt-5">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Scale className="h-4 w-4 text-blaze" /> Difference Breakdown
            </DialogTitle>
            {(() => {
              const travelOwed = diffJobs.reduce((a, l) => a + (l.travel_share || 0), 0);
              const travelPaid = diffJobs.filter((l) => l.travel_paid).reduce((a, l) => a + (l.travel_share || 0), 0);
              const stairsOwed = diffJobs.reduce((a, l) => a + (l.stairs_share || 0), 0);
              const stairsPaid = diffJobs.filter((l) => l.stairs_paid).reduce((a, l) => a + (l.stairs_share || 0), 0);
              const rateOwed = diffJobs.reduce((a, l) => a + Math.max(l.rate_gap || 0, 0), 0);
              const travelPend = travelOwed - travelPaid;
              const stairsPend = stairsOwed - stairsPaid;
              const totalPend = travelPend + stairsPend + rateOwed;
              const PaidPend = ({ paid, pend, kind }) => (
                <div className="mt-0.5 flex items-center justify-between">
                  <span><span className="text-green-700">Paid {aud(paid)}</span><span className="text-slate-300"> · </span><span className={pend > 0.5 ? "font-bold text-red-600" : "text-slate-400"}>Pending {aud(pend)}</span></span>
                  <button onClick={() => markAllPaid(kind, pend > 0.5)} className={`rounded-md px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider active:scale-95 ${pend > 0.5 ? "bg-green-600 text-white" : "border border-slate-300 bg-white text-slate-500"}`} data-testid={`buddy-diff-markall-${kind}`}>{pend > 0.5 ? "Mark all paid" : "Paid ✓"}</button>
                </div>
              );
              return (
                <div className="mt-2 space-y-2 rounded-xl bg-slate-50 p-3 text-[11px]">
                  {travelOwed > 0.5 && (
                    <div data-testid="buddy-diff-src-travel">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1 font-bold text-blaze"><Navigation className="h-3 w-3" />Travel</span>
                        <span className="text-slate-500">owed <b className="text-slate-800">{aud(travelOwed)}</b></span>
                      </div>
                      <PaidPend paid={travelPaid} pend={travelPend} kind="travel" />
                    </div>
                  )}
                  {stairsOwed > 0.5 && (
                    <div className={travelOwed > 0.5 ? "border-t border-slate-200 pt-2" : ""} data-testid="buddy-diff-src-stairs">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1 font-bold text-orange-600"><Layers className="h-3 w-3" />Stairs / extras</span>
                        <span className="text-slate-500">owed <b className="text-orange-700">{aud(stairsOwed)}</b></span>
                      </div>
                      <PaidPend paid={stairsPaid} pend={stairsPend} kind="stairs" />
                    </div>
                  )}
                  {rateOwed > 0.5 && (
                    <div className="flex items-center justify-between border-t border-slate-200 pt-2" data-testid="buddy-diff-src-rate">
                      <span className="flex items-center gap-1 font-bold text-blue-600"><Scale className="h-3 w-3" />Rate difference</span>
                      <span className="text-slate-500">owed <b className="text-blue-600">{aud(rateOwed)}</b><span className="text-slate-300"> · </span><span className="font-bold text-red-600">Pending {aud(rateOwed)}</span></span>
                    </div>
                  )}
                  <div className="flex items-center justify-between border-t border-slate-200 pt-2">
                    <span className="font-bold uppercase tracking-wider text-slate-500">Total pending</span>
                    <span className="font-display text-lg font-bold text-amber-600" data-testid="buddy-diff-total">{aud(totalPend)}</span>
                  </div>
                </div>
              );
            })()}
            <p className="mt-1.5 text-left text-[11px] text-slate-500">{diffJobs.length} job{diffJobs.length === 1 ? "" : "s"} owed travel / stairs / rate on top of the 63% contract</p>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto px-4 py-3">
            {diffJobs.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-500">No difference — every job is paid correctly per the 63% contract.</p>
            ) : (
              <div className="space-y-2">
                {diffJobs.map((l) => {
                  const hasTravel = (l.travel_share || 0) > 0.5;
                  const hasStairs = (l.stairs_share || 0) > 0.5;
                  const rateOwed = Math.max(l.rate_gap || 0, 0);
                  const jobPend = (hasTravel && !l.travel_paid ? l.travel_share : 0) + (hasStairs && !l.stairs_paid ? l.stairs_share : 0) + (rateOwed > 0.5 ? rateOwed : 0);
                  const settled = jobPend <= 0.5;
                  return (
                  <div key={l.ccrm_id} className={`rounded-xl border p-3 ${settled ? "border-green-200 bg-green-50/40" : "border-slate-200 bg-white"}`} data-testid={`buddy-diff-row-${l.ccrm_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-blaze">MB #{l.ccrm_job_number}</p>
                        <p className="text-xs font-semibold text-slate-800">{l.first_name} {l.last_name}</p>
                        <p className="text-[10px] text-slate-400">{fmtDate(l.moving_date)}</p>
                      </div>
                      <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-bold ${settled ? "border-green-300 bg-green-50 text-green-700" : "border-red-300 bg-red-50 text-red-600"}`}>
                        {settled ? "Settled" : `Pending ${aud(jobPend)}`}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                      <span className="rounded bg-slate-100 px-2 py-1 text-slate-600">Should be {aud(l.expected_payout)}</span>
                      <span className="rounded bg-slate-100 px-2 py-1 text-slate-600">MB shows {aud(l.mb_payout)}</span>
                      {hasTravel && (
                        <span className="rounded bg-amber-50 px-2 py-1 font-semibold text-amber-700">
                          Travel {l.travel_min}min ({l.both_far ? `${l.pickup_km}km & ${l.drop_km}km` : `${l.travel_far_km}km`}) = +{aud(l.travel_share)}
                        </span>
                      )}
                      {hasStairs && (
                        <span className="rounded bg-orange-50 px-2 py-1 font-semibold text-orange-700">Stairs = +{aud(l.stairs_share)}</span>
                      )}
                      {rateOwed > 0.5 && (
                        <span className="rounded bg-blue-50 px-2 py-1 font-semibold text-blue-700">Rate/hours gap {aud(rateOwed)}</span>
                      )}
                    </div>
                    {(hasTravel || hasStairs) && (
                      <div className="mt-2 flex gap-2">
                        {hasTravel && (
                          <button
                            onClick={() => markTravelPaid(l.ccrm_id, !l.travel_paid)}
                            className={`flex-1 rounded-lg py-2 text-[10px] font-bold uppercase tracking-wider active:scale-[0.98] ${l.travel_paid ? "border border-green-300 bg-green-50 text-green-700" : "bg-blaze text-white"}`}
                            data-testid={`buddy-travel-paid-${l.ccrm_id}`}
                          >
                            Travel {l.travel_paid ? `paid ✓ ${aud(l.travel_share)}` : `· pay ${aud(l.travel_share)}`}
                          </button>
                        )}
                        {hasStairs && (
                          <button
                            onClick={() => markStairsPaid(l.ccrm_id, !l.stairs_paid)}
                            className={`flex-1 rounded-lg py-2 text-[10px] font-bold uppercase tracking-wider active:scale-[0.98] ${l.stairs_paid ? "border border-green-300 bg-green-50 text-green-700" : "bg-orange-500 text-white"}`}
                            data-testid={`buddy-stairs-paid-${l.ccrm_id}`}
                          >
                            Stairs {l.stairs_paid ? `paid ✓ ${aud(l.stairs_share)}` : `· pay ${aud(l.stairs_share)}`}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pending / MB Unpaid by-week breakdown */}
      <Dialog open={!!pendingMode} onOpenChange={(v) => !v && setPendingMode(null)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-hidden p-0" data-testid="buddy-pending-modal">
          {(() => {
            const isUnpaid = pendingMode === "unpaid";
            const rows = weekly.filter((w) => (isUnpaid ? (w.unpaid_total || 0) : Math.max(w.pending || 0, w.diff_pending || 0)) > 0.5);
            const total = isUnpaid ? (s.unpaid_amount || 0) : (s.pending_total || 0);
            const totTravel = weekly.reduce((a, w) => a + (w.travel_unpaid || 0), 0);
            const totStairs = weekly.reduce((a, w) => a + (w.stairs_unpaid || 0), 0);
            const totRate = weekly.reduce((a, w) => a + (w.rate_owed || 0), 0);
            const Cat = ({ label, val, color }) => (
              <div className="flex-1 rounded-lg bg-white px-2 py-1.5 text-center">
                <p className={`font-display text-sm font-bold ${color}`}>{aud(val)}</p>
                <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
              </div>
            );
            return (
              <>
                <DialogHeader className="border-b border-slate-100 px-5 pb-3 pt-5">
                  <DialogTitle className="flex items-center gap-2 text-base">
                    {isUnpaid ? <Wallet className="h-4 w-4 text-amber-500" /> : <Clock className="h-4 w-4 text-amber-500" />}
                    {isUnpaid ? "MB Unpaid by Week" : "Pending by Week"}
                  </DialogTitle>
                  <div className="mt-2 flex items-center justify-between rounded-xl bg-amber-50 px-3 py-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-amber-600">
                      {isUnpaid ? "Completed jobs MB hasn't paid" : "MB owes − received"}
                    </span>
                    <span className="font-display text-xl font-bold text-amber-600" data-testid="buddy-pending-total">{aud(total)}</span>
                  </div>
                  {!isUnpaid && (totTravel + totStairs + totRate) > 0.5 && (
                    <div className="mt-2 flex gap-1.5 rounded-xl bg-slate-100 p-1.5" data-testid="buddy-pending-cats">
                      <Cat label="Travel" val={totTravel} color="text-blaze" />
                      <Cat label="Stairs" val={totStairs} color="text-orange-600" />
                      {totRate > 0.5 && <Cat label="Rate" val={totRate} color="text-blue-600" />}
                    </div>
                  )}
                  {!isUnpaid && (
                    <div className="mt-2 flex items-center justify-between rounded-xl border-2 border-amber-300 bg-amber-100 px-3 py-2" data-testid="buddy-pending-grandtotal">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700">Total balance · MB + travel + stairs</span>
                      <span className="font-display text-xl font-bold text-amber-700">{aud((s.pending_total || 0) + (s.difference_outstanding || 0))}</span>
                    </div>
                  )}
                  <p className="mt-1.5 text-left text-[11px] text-slate-500">{rows.length} week{rows.length === 1 ? "" : "s"} · tap a week for its breakdown</p>
                </DialogHeader>
                <div className="max-h-[55vh] overflow-y-auto px-4 py-3">
                  {rows.length === 0 ? (
                    <p className="py-8 text-center text-sm text-slate-500">{isUnpaid ? "Every completed job is marked paid by Movers Buddy." : "Nothing pending — all received."}</p>
                  ) : isUnpaid ? (
                    <div className="space-y-2">
                      {rows.map((w) => (
                        <button
                          key={w.week_start}
                          onClick={() => { setView("jobs"); setShowWeekly(true); setExpandedWeek(w.week_start); setPendingMode(null); }}
                          className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition-colors hover:border-amber-300 hover:bg-amber-50/40"
                          data-testid={`buddy-pending-week-${w.week_start}`}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-slate-900">{weekLabel(w.week_start, w.week_end)}</p>
                            <p className="text-[11px] text-slate-500">MB total {aud(w.mb_total)} · paid {aud(w.paid_total)}</p>
                          </div>
                          <span className="shrink-0 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-[12px] font-bold text-amber-700">{aud(w.unpaid_total)}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {rows.map((w) => {
                        const open = expPendWeek === w.week_start;
                        return (
                          <div key={w.week_start} className="rounded-xl border border-slate-200 bg-white" data-testid={`buddy-pending-week-${w.week_start}`}>
                            <button
                              onClick={() => setExpPendWeek(open ? null : w.week_start)}
                              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                            >
                              <div className="min-w-0">
                                <p className="text-sm font-bold text-slate-900">{weekLabel(w.week_start, w.week_end)}</p>
                                <p className="text-[11px] text-slate-500">MB total {aud(w.mb_total)} · received {aud(w.received_total)}</p>
                              </div>
                              <span className="flex shrink-0 items-center gap-1">
                                <span className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-[12px] font-bold text-amber-700">{aud(w.pending)}</span>
                                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                              </span>
                            </button>
                            {open && (
                              <div className="border-t border-slate-100 px-4 py-3 text-[11px]" data-testid={`buddy-pending-breakdown-${w.week_start}`}>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500">MB balance (owes − received)</span>
                                  <span className="font-bold text-amber-600">{aud(w.pending)}</span>
                                </div>
                                {w.travel_unpaid > 0.5 && (
                                  <div className="mt-1.5 flex items-center justify-between">
                                    <span className="flex items-center gap-1 text-blaze"><Navigation className="h-3 w-3" />Travel pending</span>
                                    <span className="font-bold text-blaze">{aud(w.travel_unpaid)}</span>
                                  </div>
                                )}
                                {w.stairs_unpaid > 0.5 && (
                                  <div className="mt-1.5 flex items-center justify-between">
                                    <span className="flex items-center gap-1 text-orange-600"><Layers className="h-3 w-3" />Stairs pending</span>
                                    <span className="font-bold text-orange-600">{aud(w.stairs_unpaid)}</span>
                                  </div>
                                )}
                                {w.rate_owed > 0.5 && (
                                  <div className="mt-1.5 flex items-center justify-between">
                                    <span className="flex items-center gap-1 text-blue-600"><Scale className="h-3 w-3" />Rate pending</span>
                                    <span className="font-bold text-blue-600">{aud(w.rate_owed)}</span>
                                  </div>
                                )}
                                <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2">
                                  <span className="font-bold uppercase tracking-wider text-slate-700">Total balance</span>
                                  <span className="font-display text-base font-bold text-amber-700" data-testid={`buddy-pending-weektotal-${w.week_start}`}>{aud((w.pending || 0) + (w.diff_pending || 0))}</span>
                                </div>
                                <button
                                  onClick={() => { setView("jobs"); setShowWeekly(true); setExpandedWeek(w.week_start); setPendingMode(null); }}
                                  className="mt-2.5 w-full rounded-lg bg-blaze py-2 text-[10px] font-bold uppercase tracking-wider text-white active:scale-[0.98]"
                                  data-testid={`buddy-pending-open-${w.week_start}`}
                                >
                                  Open week · record payment
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Full job breakdown */}
      <Dialog open={!!detailLead} onOpenChange={(v) => !v && setDetailLead(null)}>
        <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto p-0" data-testid="buddy-detail-modal">
          {detailLead && (
            <>
              <DialogHeader className="border-b border-slate-100 px-5 pb-3 pt-5">
                <DialogTitle className="flex items-center gap-2 text-base">
                  <Inbox className="h-4 w-4 text-blaze" /> MB #{detailLead.ccrm_job_number} — full breakdown
                </DialogTitle>
                <p className="text-left text-[12px] font-semibold text-slate-700">{detailLead.first_name} {detailLead.last_name}</p>
                <p className="text-left text-[11px] text-slate-400">{fmtDate(detailLead.moving_date)} · {detailLead.job_status}{detailLead.start_time ? ` · ${detailLead.start_time}` : ""}</p>
              </DialogHeader>
              <div className="px-4 pb-5">
                <HoursCompare l={detailLead} />
                <Reconcile l={detailLead} />
                <CustomerCharge l={detailLead} onSetExtras={setExtras} />
                {detailLead.converted_job_id && (
                  <p className="mt-2 rounded-lg bg-blue-50 px-2.5 py-2 text-[11px] font-bold text-blaze">
                    <Users className="mr-1.5 inline h-3.5 w-3.5" />
                    {detailLead.converted_job_id}: {(detailLead.our_assigned || []).length ? `Assigned to ${detailLead.our_assigned.join(", ")}` : "Not assigned in app"}
                  </p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
